// src/pages/Media.jsx

import React, { useState, useEffect } from 'react';
import { useWabaAccounts, withAccount } from '../lib/waba';
import {
  Container,
  Select,
  MenuItem,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Typography,
  styled,
  Chip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { CloudUpload, InsertDriveFile, Image } from '@mui/icons-material';
import { MaterialReactTable } from 'material-react-table';

const ModernPaper = styled('div')(({ theme }) => ({
  borderRadius: '16px',
  boxShadow: '0px 4px 24px rgba(0,0,0,0.08)',
  backgroundColor: '#fff',
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(4),
    marginBottom: theme.spacing(6)
  },
  transition: 'transform 0.2s ease-in-out',
  '&:hover': { transform: 'translateY(-2px)' }
}));

const GradientButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#F96168',
  color: 'white',
  fontWeight: 600,
  padding: '12px 24px',
  borderRadius: '12px',
  textTransform: 'none',
  '&:hover': { backgroundColor: '#EA434B' }
}));

export default function MediaManager() {
  const [selectedType, setSelectedType] = useState('Image');
  const [fileName, setFileName] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadResult, setUploadResult] = useState({ success: false, message: '' });
  const [uploadModalOpen, setUploadModalOpen] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const [mediaList, setMediaList] = useState([]);

  // The library is per WhatsApp account: what is listed, uploaded and deleted
  // here all belongs to the account chosen in the header.
  const { selected: wabaAccount } = useWabaAccounts();

  // Fetch current media entries from the server
  const fetchMediaList = async () => {
    if (!wabaAccount) return;
    try {
      const resp = await fetch(`/api${withAccount('/media/', wabaAccount)}`);
      if (!resp.ok) throw new Error(`Error ${resp.status}`);
      const data = await resp.json();
      setMediaList(data);
    } catch (err) {
      console.error('Failed to fetch media list:', err);
    }
  };

  useEffect(() => {
    setMediaList([]);
    fetchMediaList();
  }, [wabaAccount]);

  const handleFileChange = e => {
    setSelectedFile(e.target.files[0] || null);
  };

  const handleUpload = async () => {
    if (!selectedFile || !fileName) {
      setUploadResult({ success: false, message: 'Please pick a file and enter a name.' });
      setUploadModalOpen(true);
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('fileName', fileName);
    formData.append('type', selectedType);
    formData.append('waba_account', wabaAccount);

    try {
      const resp = await fetch('/api/media/upload', {
        method: 'POST',
        body: formData
      });
      const result = await resp.json();

      if (!resp.ok) {
        throw new Error(result.detail || 'Upload failed');
      }

      setUploadResult({ success: true, message: `Saved as ${result.filename}` });
      await fetchMediaList();
    } catch (err) {
      console.error(err);
      setUploadResult({ success: false, message: err.message });
    } finally {
      setUploadModalOpen(true);
      setSelectedFile(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      const resp = await fetch(`/api${withAccount(`/media/${deleteTarget.uniqueId}`, wabaAccount)}`, { method: 'DELETE' });
      if (!resp.ok) throw new Error(`Error ${resp.status}`);
      await fetchMediaList();
    } catch (err) {
      console.error('Delete failed:', err);
      // optionally show an error modal
    } finally {
      setDeleteModalOpen(false);
      setDeleteTarget(null);
    }
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // uniqueId is a long opaque string — the first thing to drop on a phone
  const hiddenColumns = isMobile ? ['uniqueId'] : [];

  const columns = [
    {
      accessorKey: 'type',
      header: 'Type',
      Cell: ({ cell }) => (
        <Chip
          label={cell.getValue()}
          sx={{
            bgcolor: cell.getValue() === 'Image' ? '#eef2ff' : '#f5f3ff',
            color: cell.getValue() === 'Image' ? '#D1272F' : '#000000',
            borderRadius: '6px'
          }}
        />
      )
    },
    { accessorKey: 'name', header: 'Name', muiTableBodyCellProps: { sx: { fontWeight: 500 } } },
    {
      accessorKey: 'status',
      header: 'Status',
      Cell: ({ cell }) => (
        <Chip
          label={cell.getValue()}
          color="success"
          variant="outlined"
          size="small"
        />
      )
    },
    {
      accessorKey: 'uniqueId',
      header: 'Filename',
      muiTableBodyCellProps: { sx: { color: '#6b7280' } }
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      Cell: ({ row }) => (
        <Button
          variant="contained"
          color="error"
          size="small"
          onClick={() => {
            setDeleteTarget(row.original);
            setDeleteModalOpen(true);
          }}
          sx={{ textTransform: 'none' }}
        >
          Delete
        </Button>
      )
    }
  ];

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 2, sm: 4 }, mb: { xs: 4, sm: 8 }, px: { xs: 2, sm: 3 } }}>
      <h2 className="mb-4 text-xl sm:text-2xl font-bold text-gray-900">
        Media Manager
      </h2>

      <ModernPaper>
        <Grid container spacing={{ xs: 2, sm: 4 }} alignItems="center">
          <Grid item xs={12} md={3}>
            <Select
              fullWidth
              value={selectedType}
              onChange={e => setSelectedType(e.target.value)}
              sx={{
                borderRadius: '12px',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' }
              }}
            >
              <MenuItem value="Image">
                <Image fontSize="small" /> Image
              </MenuItem>
              <MenuItem value="PDF">
                <InsertDriveFile fontSize="small" /> PDF
              </MenuItem>
            </Select>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="File Name"
              value={fileName}
              onChange={e => setFileName(e.target.value)}
              variant="outlined"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Grid>
          <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, gap: 2, minWidth: 0 }}>
            <input
              type="file"
              accept={selectedType === 'Image' ? 'image/*' : 'application/pdf'}
              hidden
              id="media-upload"
              onChange={handleFileChange}
            />
            <label htmlFor="media-upload">
              <GradientButton component="span" startIcon={<CloudUpload />}>
                Choose File
              </GradientButton>
            </label>
            {selectedFile && (
              <Typography noWrap sx={{ minWidth: 0, fontSize: { xs: 14, sm: 16 } }} title={selectedFile.name}>
                {selectedFile.name}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} md={2}>
            <GradientButton
              fullWidth
              onClick={handleUpload}
              disabled={!selectedFile || !fileName}
              endIcon={<CloudUpload />}
            >
              Upload Now
            </GradientButton>
          </Grid>
        </Grid>
      </ModernPaper>

      {/* Upload result modal */}
      <Dialog open={uploadModalOpen} onClose={() => setUploadModalOpen(false)}>
        <DialogTitle>
          {uploadResult.success ? '🎉 Success!' : '⚠️ Error'}
        </DialogTitle>
        <DialogContent>
          <Typography>{uploadResult.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadModalOpen(false)} sx={{ textTransform: 'none' }}>
            Dismiss
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation modal */}
      <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Do you want to delete “{deleteTarget?.name}”?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteModalOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" sx={{ textTransform: 'none' }}>
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      <ModernPaper>
        <MaterialReactTable
          columns={columns}
          data={mediaList}
          enableSorting
          enablePagination
          rowNumberMode="original"
          initialState={{
            density: isMobile ? 'compact' : 'comfortable',
            pagination: { pageSize: isMobile ? 5 : 10 },
            columnVisibility: Object.fromEntries(hiddenColumns.map((c) => [c, false]))
          }}
          state={{ columnVisibility: Object.fromEntries(hiddenColumns.map((c) => [c, false])) }}
          muiTablePaperProps={{ sx: { borderRadius: 3, border: '1px solid #f1f5f9' } }}
          muiTableContainerProps={{ sx: { maxHeight: { xs: 420, sm: 'none' } } }}
        />
      </ModernPaper>
    </Container>
  );
}
